import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { auditLogsApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  History,
  Search,
  User,
  Clock,
  Edit,
  Plus,
  Trash2,
  FileText,
  Loader2,
  Code2,
  Sparkles,
  Bug,
  ShieldCheck,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const AuditLog = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [activeTab, setActiveTab] = useState('audit');
  const [codeUpdates, setCodeUpdates] = useState([]);
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [newUpdate, setNewUpdate] = useState({
    title: '',
    update_type: 'feature',
    category: 'feature',
    summary: '',
    reason: '',
    areas_affected: '',
    files_modules: '',
    testing_completed: '',
    known_issues: '',
    description: '',
    version: '',
    update_date: format(new Date(), 'yyyy-MM-dd'),
  });
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [viewingUpdate, setViewingUpdate] = useState(null);

  // Only Admin and Accounting Admin can access audit logs
  const canAccessAuditLog = user?.role === 'admin' || user?.role === 'accounting_admin';

  useEffect(() => {
    if (canAccessAuditLog) {
      loadLogs();
      loadCodeUpdates();
    }
  }, [canAccessAuditLog]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const data = await auditLogsApi.getAll({ limit: 200 });
      setLogs(data);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCodeUpdates = async () => {
    try {
      const data = await auditLogsApi.listCodeUpdates(200);
      setCodeUpdates(data);
    } catch (error) {
      console.error('Failed to load code updates:', error);
    }
  };

  const handleAddCodeUpdate = async () => {
    if (!newUpdate.title.trim()) return;
    setSavingUpdate(true);
    try {
      const created = await auditLogsApi.createCodeUpdate(newUpdate);
      setCodeUpdates([created, ...codeUpdates]);
      setShowAddUpdate(false);
      setNewUpdate({
        title: '',
        update_type: 'feature',
        category: 'feature',
        summary: '',
        reason: '',
        areas_affected: '',
        files_modules: '',
        testing_completed: '',
        known_issues: '',
        description: '',
        version: '',
        update_date: format(new Date(), 'yyyy-MM-dd'),
      });
    } catch (error) {
      alert('Failed to add code update: ' + error.message);
    } finally {
      setSavingUpdate(false);
    }
  };

  const handleDeleteCodeUpdate = async (id) => {
    if (!window.confirm('Delete this code update?')) return;
    try {
      await auditLogsApi.deleteCodeUpdate(id);
      setCodeUpdates(codeUpdates.filter(u => u.id !== id));
    } catch (error) {
      alert('Failed to delete: ' + error.message);
    }
  };

  const categoryStyle = (cat) => {
    switch (cat) {
      case 'feature': return { label: 'Feature', color: 'bg-purple-100 text-purple-700', icon: Sparkles };
      case 'bugfix': return { label: 'Bug Fix', color: 'bg-red-100 text-red-700', icon: Bug };
      case 'improvement': return { label: 'Improvement', color: 'bg-blue-100 text-blue-700', icon: Edit };
      case 'security': return { label: 'Security', color: 'bg-amber-100 text-amber-700', icon: ShieldCheck };
      default: return { label: 'Other', color: 'bg-gray-100 text-gray-700', icon: FileText };
    }
  };

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const matchesSearch = 
        log.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        log.entity_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        JSON.stringify(log.changes)?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = filterType === 'all' || log.entity_type === filterType;
      const matchesAction = filterAction === 'all' || log.action === filterAction;
      return matchesSearch && matchesType && matchesAction;
    });
  }, [logs, searchQuery, filterType, filterAction]);

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE': return <Plus className="w-4 h-4" />;
      case 'UPDATE': return <Edit className="w-4 h-4" />;
      case 'DELETE': return <Trash2 className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-purple-100 text-purple-700';
      case 'UPDATE': return 'bg-blue-100 text-blue-700';
      case 'DELETE': return 'bg-red-100 text-red-700';
      case 'REVIEW': return 'bg-purple-100 text-purple-700';
      case 'COPY_WEEK': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatChanges = (changes) => {
    if (!changes || typeof changes !== 'object') return '-';
    return Object.entries(changes)
      .filter(([key]) => !['hashed_password', 'password'].includes(key))
      .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${typeof value === 'object' ? JSON.stringify(value) : value}`)
      .join(', ');
  };

  const entityTypes = [...new Set(logs.map(l => l.entity_type))].filter(Boolean);
  const actionTypes = [...new Set(logs.map(l => l.action))].filter(Boolean);

  if (!canAccessAuditLog) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <History className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500 mt-2">
            Only Admin and Accounting Admin users can view audit logs.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-500 mt-1">Track all changes and development updates</p>
        </div>
        {activeTab === 'updates' && (
          <Button onClick={() => setShowAddUpdate(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-code-update-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Code Update
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="audit" data-testid="audit-tab"><History className="w-4 h-4 mr-2" />Audit Log ({logs.length})</TabsTrigger>
          <TabsTrigger value="updates" data-testid="code-updates-tab"><Code2 className="w-4 h-4 mr-2" />Code Updates ({codeUpdates.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'updates' ? (
        <Card className="border-0 shadow-md" data-testid="code-updates-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Date</TableHead>
                  <TableHead>Update Type</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>Areas Affected</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codeUpdates.map((upd) => {
                  const cat = categoryStyle(upd.category);
                  const CatIcon = cat.icon;
                  return (
                    <TableRow key={upd.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setViewingUpdate(upd)} data-testid={`code-update-row-${upd.id}`}>
                      <TableCell className="text-sm text-gray-600">{format(parseISO(upd.update_date || upd.timestamp), 'MMM d, yyyy')}</TableCell>
                      <TableCell><Badge className={cat.color}><CatIcon className="w-3 h-3 mr-1" />{upd.update_type || cat.label}</Badge></TableCell>
                      <TableCell className="font-medium text-gray-900">{upd.title}</TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-md"><p className="line-clamp-2">{upd.summary || upd.description || '-'}</p></TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-xs"><p className="line-clamp-2">{upd.areas_affected || '-'}</p></TableCell>
                      <TableCell className="text-sm text-gray-500">{upd.created_by_name}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteCodeUpdate(upd.id)} data-testid={`delete-code-update-${upd.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {codeUpdates.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Code2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No code updates logged yet</p>
                <Button onClick={() => setShowAddUpdate(true)} className="mt-3 bg-purple-600 hover:bg-purple-700">
                  <Plus className="w-4 h-4 mr-2" />Add Your First Entry
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
      <>
      {/* Filters */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {entityTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionTypes.map(action => (
                  <SelectItem key={action} value={action}>{action}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Changes</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{logs.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-xl"><History className="w-6 h-6 text-blue-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Creates</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">{logs.filter(l => l.action === 'CREATE').length}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-xl"><Plus className="w-6 h-6 text-purple-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Updates</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{logs.filter(l => l.action === 'UPDATE').length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-xl"><Edit className="w-6 h-6 text-blue-600" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Deletes</p>
                <p className="text-3xl font-bold text-red-600 mt-1">{logs.filter(l => l.action === 'DELETE').length}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-xl"><Trash2 className="w-6 h-6 text-red-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Logs Table */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Changes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id} className="hover:bg-gray-50">
                  <TableCell>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm">{format(parseISO(log.timestamp), 'MMM d, yyyy')}</p>
                        <p className="text-xs text-gray-400">{format(parseISO(log.timestamp), 'h:mm:ss a')}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{log.user_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getActionColor(log.action)}>
                      {getActionIcon(log.action)}
                      <span className="ml-1">{log.action}</span>
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{log.entity_type}</p>
                      <p className="text-xs font-mono text-gray-400">{log.entity_id}</p>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-xs">
                    <p className="text-sm text-gray-600 truncate" title={formatChanges(log.changes)}>
                      {formatChanges(log.changes)}
                    </p>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredLogs.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No audit logs found</p>
            </div>
          )}
        </CardContent>
      </Card>
      </>
      )}

      {/* Add Code Update Modal */}
      <Dialog open={showAddUpdate} onOpenChange={setShowAddUpdate}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Log Code Update</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newUpdate.update_date}
                  onChange={(e) => setNewUpdate({ ...newUpdate, update_date: e.target.value })}
                  data-testid="code-update-date-input"
                />
              </div>
              <div>
                <Label>Update Type *</Label>
                <Select value={newUpdate.update_type} onValueChange={(v) => setNewUpdate({ ...newUpdate, update_type: v, category: v === 'Bug Fix' ? 'bugfix' : v === 'Security' ? 'security' : v === 'Improvement' ? 'improvement' : 'feature' })}>
                  <SelectTrigger data-testid="code-update-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Feature">Feature</SelectItem>
                    <SelectItem value="Bug Fix">Bug Fix</SelectItem>
                    <SelectItem value="Improvement">Improvement</SelectItem>
                    <SelectItem value="Security">Security</SelectItem>
                    <SelectItem value="Refactor">Refactor</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                placeholder="e.g., Payroll & Time Entry Bug Fixes — Breaks, Overtime, Overnight"
                value={newUpdate.title}
                onChange={(e) => setNewUpdate({ ...newUpdate, title: e.target.value })}
                data-testid="code-update-title-input"
              />
            </div>
            <div>
              <Label>Summary</Label>
              <Textarea
                placeholder="One-line summary of what changed"
                rows={2}
                value={newUpdate.summary}
                onChange={(e) => setNewUpdate({ ...newUpdate, summary: e.target.value })}
                data-testid="code-update-summary-input"
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea
                placeholder="Why this update was needed"
                rows={2}
                value={newUpdate.reason}
                onChange={(e) => setNewUpdate({ ...newUpdate, reason: e.target.value })}
                data-testid="code-update-reason-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Areas Affected</Label>
                <Textarea
                  placeholder="e.g., Time Entries, Payroll, Dashboard"
                  rows={2}
                  value={newUpdate.areas_affected}
                  onChange={(e) => setNewUpdate({ ...newUpdate, areas_affected: e.target.value })}
                  data-testid="code-update-areas-input"
                />
              </div>
              <div>
                <Label>Files / Modules Changed</Label>
                <Textarea
                  placeholder="e.g., time_calc.py, TimeEntries.jsx"
                  rows={2}
                  value={newUpdate.files_modules}
                  onChange={(e) => setNewUpdate({ ...newUpdate, files_modules: e.target.value })}
                  data-testid="code-update-files-input"
                />
              </div>
            </div>
            <div>
              <Label>Testing Completed</Label>
              <Textarea
                placeholder="What was tested and how"
                rows={2}
                value={newUpdate.testing_completed}
                onChange={(e) => setNewUpdate({ ...newUpdate, testing_completed: e.target.value })}
                data-testid="code-update-testing-input"
              />
            </div>
            <div>
              <Label>Known Issues</Label>
              <Textarea
                placeholder="Any caveats, edge cases or follow-ups"
                rows={2}
                value={newUpdate.known_issues}
                onChange={(e) => setNewUpdate({ ...newUpdate, known_issues: e.target.value })}
                data-testid="code-update-known-issues-input"
              />
            </div>
            <div>
              <Label>Version (optional)</Label>
              <Input
                placeholder="e.g., v1.4.2"
                value={newUpdate.version}
                onChange={(e) => setNewUpdate({ ...newUpdate, version: e.target.value })}
              />
            </div>
            <div>
              <Label>Additional Notes / Description</Label>
              <Textarea
                placeholder="Any extra details..."
                rows={3}
                value={newUpdate.description}
                onChange={(e) => setNewUpdate({ ...newUpdate, description: e.target.value })}
                data-testid="code-update-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUpdate(false)}>Cancel</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleAddCodeUpdate}
              disabled={!newUpdate.title.trim() || savingUpdate}
              data-testid="save-code-update-btn"
            >
              {savingUpdate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Code Update Details Modal */}
      <Dialog open={!!viewingUpdate} onOpenChange={() => setViewingUpdate(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="view-code-update-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code2 className="w-5 h-5" />
              {viewingUpdate?.title}
            </DialogTitle>
          </DialogHeader>
          {viewingUpdate && (() => {
            const cat = categoryStyle(viewingUpdate.category);
            const CatIcon = cat.icon;
            const renderField = (label, value) => (
              <div key={label} className="border-l-2 border-purple-200 pl-3 py-1">
                <p className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{label}</p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap mt-0.5">{value || <span className="text-gray-400 italic">—</span>}</p>
              </div>
            );
            return (
              <div className="space-y-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={cat.color}><CatIcon className="w-3 h-3 mr-1" />{viewingUpdate.update_type || cat.label}</Badge>
                  <Badge variant="outline">{format(parseISO(viewingUpdate.update_date || viewingUpdate.timestamp), 'MMM d, yyyy')}</Badge>
                  {viewingUpdate.version && <Badge variant="outline" className="font-mono">{viewingUpdate.version}</Badge>}
                  <span className="text-xs text-gray-500 ml-auto">by {viewingUpdate.created_by_name}</span>
                </div>
                {renderField('Summary', viewingUpdate.summary)}
                {renderField('Reason', viewingUpdate.reason)}
                {renderField('Areas Affected', viewingUpdate.areas_affected)}
                {renderField('Files / Modules Changed', viewingUpdate.files_modules)}
                {renderField('Testing Completed', viewingUpdate.testing_completed)}
                {renderField('Known Issues', viewingUpdate.known_issues)}
                {viewingUpdate.description && renderField('Additional Notes', viewingUpdate.description)}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingUpdate(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLog;
